import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useDarkMode } from './DarkModeContext';
// DATEDAY: date-only values shown as their calendar day in every timezone.
import { toDisplayDate } from './dateOnly';

const API = '/api';
// CANCELHIST: the same green/grey pair the rest of the portal already uses for a settled
// outcome (LeaveDeclarationForm's approved and cancelled badges use exactly these), so a
// decision reads the same here as it does on the employee's own leave page.
const OK_BG = '#d1fae5';
const OK_FG = '#065f46';
const NO_BG = '#e5e7eb';
const NO_FG = '#4b5563';

function fmt(d) {
  if (!d) return '-';
  return toDisplayDate(d).toLocaleDateString('en-GB');
}

function fmtDateTime(d) {
  if (!d) return '-';
  const dt = toDisplayDate(d);
  return `${dt.toLocaleDateString('en-GB')} ${dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
}

// CANCELHIST: the third section of the Validation page, a READ ONLY record of the cancellation
// requests this manager has actually decided.
//
// It shows ONLY decisions this manager made, which is the same scope the pending section above
// has: the server filters on the declaration's approver_id, the identical column and helper
// list_pending_cancellations uses. Nothing here widens what anyone can see.
//
// FREE CANCELLATIONS ARE ABSENT BY DESIGN, not by omission. A cancellation made before the lock
// never involved the manager, so surfacing it would show them leave decisions they were never
// party to. The server excludes them by action name; this component simply renders what it is
// given and never asks for more.
export default function LeaveCancellationHistorySection() {
  const { isDarkMode } = useDarkMode();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Same split the two sections above use: a failed LOAD must suppress the empty state, so a
  // fetch failure is never mistaken for a genuinely empty history.
  const [loadFailed, setLoadFailed] = useState(false);

  const cardBg = isDarkMode ? '#1a2744' : '#ffffff';
  const textCol = isDarkMode ? '#e8e4d8' : '#1a1a2e';
  const mutedCol = isDarkMode ? '#9ba8c0' : '#666';
  const border = isDarkMode ? '1px solid rgba(201,168,76,0.25)' : '1px solid #e2e6ec';
  const rowBorder = isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #f0f0f0';

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/leave/cancellation-history`)
      .then(res => { setRows(res.data || []); setError(''); setLoadFailed(false); })
      .catch(() => {
        setRows([]);
        setError('Could not load the cancellation history - please refresh.');
        setLoadFailed(true);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ background: cardBg, border, borderRadius: '10px', padding: '28px', marginBottom: '20px' }}>
      <h3 style={{ fontSize: '15px', fontWeight: 700, color: textCol, margin: '0 0 6px' }}>Cancellation decisions you have made</h3>
      <p style={{ fontSize: '12px', color: mutedCol, margin: '0 0 16px', lineHeight: 1.5 }}>
        A record of the cancellation requests you have already approved or refused. Cancellations
        an employee made before their leave began do not need your approval, so they are not
        listed here.
      </p>

      {error && <div style={{ fontSize: '13px', color: '#A63A3A', marginBottom: '14px' }} role="alert" aria-live="polite">{error}</div>}

      {loading ? (
        <p style={{ color: mutedCol, fontStyle: 'italic', fontSize: '13px', margin: 0 }}>Loading…</p>
      ) : loadFailed ? null : rows.length === 0 ? (
        // CANCELHIST: the empty state must read as a normal, expected state rather than a
        // failure, because it IS the normal state for most managers most of the time - a
        // cancellation request only arises for an already-approved holiday or a WFH already
        // under way. It says plainly that nothing has been decided YET, so it cannot be mistaken
        // for a screen that failed to load (which is what the separate error branch above says).
        <p style={{ color: mutedCol, fontStyle: 'italic', fontSize: '13px', margin: 0 }}>
          You have not decided any cancellation requests yet. Once you approve or refuse one, it
          will appear here.
        </p>
      ) : (
        rows.map(r => {
          const approved = r.decision === 'approved';
          return (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: rowBorder, gap: '12px' }}>
              <div style={{ minWidth: 0 }}>
                <p style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: 600, color: textCol }}>
                  {r.employee_name}
                </p>
                <p style={{ margin: 0, fontSize: '12px', color: mutedCol }}>
                  {r.leave_label} · {fmt(r.start_date)} → {fmt(r.end_date)}
                </p>
                <p style={{ margin: '4px 0 0', fontSize: '11px', color: mutedCol }}>
                  Decided {fmtDateTime(r.decided_at)}
                </p>
              </div>
              <div style={{ flexShrink: 0 }}>
                {/* The wording states the EFFECT on the leave, not just the verdict, so the row
                    is readable without having to remember which way round a cancellation
                    decision runs. */}
                <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: approved ? OK_BG : NO_BG, color: approved ? OK_FG : NO_FG, whiteSpace: 'nowrap' }}>
                  {approved ? 'Cancellation approved' : 'Cancellation refused'}
                </span>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
